import { useEffect, useState, useCallback, Fragment } from "react";
import { Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getStripeEnvironment } from "@/lib/stripe";
import { ExpensesPanel } from "./pnl/ExpensesPanel";
import { SubscriberRosterCard } from "./pnl/SubscriberRosterCard";

type FeesBreakdown = {
  processing_cents: number;
  payout_cents: number;
  billing_cents: number;
  tax_cents: number;
  dispute_cents: number;
  other_cents: number;
};

type MonthRow = {
  month: string;
  revenue_cents: number;
  refunds_cents: number;
  stripe_fees_cents: number;
  stripe_fees_source: "actual" | "estimate";
  stripe_fees_breakdown?: FeesBreakdown;
  net_revenue_cents: number;
  expenses_by_category: Record<string, number>;
  expenses_total_cents: number;
  operating_income_cents: number;
  margin_pct: number | null;
};

type Payload = {
  environment: "sandbox" | "live";
  generated_at: string;
  stripe_unavailable_reason: string | null;
  months: MonthRow[];
};

const fmt = (cents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(cents / 100);

const CATEGORY_LABEL: Record<string, string> = {
  hosting: "Hosting / Cloud",
  ai_api: "AI / API",
  contractors: "Contractors",
  salaries: "Salaries",
  tools_saas: "Tools & SaaS",
  marketing: "Marketing",
  legal_accounting: "Legal & Accounting",
  taxes: "Taxes",
  refunds_manual: "Refunds (manual)",
  other: "Other",
  ai_cogs_auto: "AI / API (auto)",
};

const FEE_BUCKET_LABEL: Record<keyof FeesBreakdown, string> = {
  processing_cents: "Processing",
  payout_cents: "Payouts",
  billing_cents: "Billing",
  tax_cents: "Tax",
  dispute_cents: "Disputes",
  other_cents: "Other",
};

function feeBreakdownEntries(b?: FeesBreakdown): Array<[string, number]> {
  if (!b) return [];
  return (Object.keys(FEE_BUCKET_LABEL) as Array<keyof FeesBreakdown>)
    .filter((k) => (b[k] ?? 0) !== 0)
    .map((k) => [FEE_BUCKET_LABEL[k], b[k]] as [string, number]);
}

export const PnlTab = () => {
  const [env, setEnv] = useState<"sandbox" | "live">(getStripeEnvironment());
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (which: "sandbox" | "live") => {
    setLoading(true);
    const { data: payload, error } = await supabase.functions.invoke("admin-pnl-overview", {
      body: { environment: which, months: 12 },
    });
    setLoading(false);
    if (error) { toast.error(`Could not load P&L: ${error.message}`); return; }
    setData(payload as Payload);
  }, []);

  useEffect(() => { void load(env); }, [env, load]);

  const exportCsv = () => {
    if (!data) return;
    const header = ["Month", "Revenue", "Refunds", "Stripe fees (est)", "Net revenue", "Expenses", "Operating income", "Margin %"];
    const rows = data.months.map((m) => [
      m.month,
      (m.revenue_cents / 100).toFixed(2),
      (m.refunds_cents / 100).toFixed(2),
      (m.stripe_fees_cents / 100).toFixed(2),
      (m.net_revenue_cents / 100).toFixed(2),
      (m.expenses_total_cents / 100).toFixed(2),
      (m.operating_income_cents / 100).toFixed(2),
      m.margin_pct == null ? "" : m.margin_pct.toFixed(1),
    ]);
    const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `pnl-${env}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const current = data?.months[data.months.length - 1];
  const chartData = (data?.months ?? []).map((m) => ({
    month: m.month.slice(5),
    Revenue: m.revenue_cents / 100,
    Expenses: m.expenses_total_cents / 100,
    Net: m.operating_income_cents / 100,
  }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-medium">P&L — operating statement</h2>
          <p className="text-xs text-muted-foreground">Monthly birds-eye view. Revenue from Stripe; expenses entered manually below.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={env} onValueChange={(v) => setEnv(v as "sandbox" | "live")}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sandbox">Test</SelectItem>
              <SelectItem value="live">Live</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => load(env)} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={!data}>
            <Download className="h-4 w-4" /> CSV
          </Button>
        </div>
      </div>

      {data?.stripe_unavailable_reason && (
        <Card><CardContent className="pt-4 text-sm text-destructive">Stripe data unavailable: {data.stripe_unavailable_reason}</CardContent></Card>
      )}

      {/* Current month summary */}
      <Card>
        <CardHeader>
          <CardTitle>{current ? `Current month — ${current.month}` : "Current month"}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading && !current && <Skeleton className="h-32 w-full" />}
          {current && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><div className="text-muted-foreground text-xs">Revenue (gross)</div><div className="text-xl font-semibold">{fmt(current.revenue_cents)}</div></div>
              <div><div className="text-muted-foreground text-xs">Refunds + fees</div><div className="text-xl font-semibold">−{fmt(current.refunds_cents + current.stripe_fees_cents)}{current.stripe_fees_source === "estimate" && <span className="text-xs text-muted-foreground"> (est.)</span>}</div></div>
              <div><div className="text-muted-foreground text-xs">Operating expenses</div><div className="text-xl font-semibold">−{fmt(current.expenses_total_cents)}</div></div>
              <div><div className="text-muted-foreground text-xs">Operating income</div><div className="text-xl font-semibold">{fmt(current.operating_income_cents)} {current.margin_pct != null && <span className="text-xs text-muted-foreground">({current.margin_pct.toFixed(1)}%)</span>}</div></div>
            </div>
          )}
          {current && Object.keys(current.expenses_by_category).length > 0 && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              {Object.entries(current.expenses_by_category).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b py-1">
                  <span className="text-muted-foreground">{CATEGORY_LABEL[k] ?? k}</span>
                  <span className="font-mono">{fmt(v)}</span>
                </div>
              ))}
            </div>
          )}
          {current?.expenses_by_category?.ai_cogs_auto != null && (
            <p className="mt-3 text-xs text-muted-foreground">"AI / API (auto)" is auto-pulled from logged AI usage — don't enter it manually.</p>
          )}
        </CardContent>
      </Card>

      {/* Trend */}
      <Card>
        <CardHeader><CardTitle>12-month trend</CardTitle></CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v: number) => `$${v.toFixed(0)}`} />
                <Legend />
                <Line type="monotone" dataKey="Revenue" stroke="hsl(var(--primary))" strokeWidth={2} />
                <Line type="monotone" dataKey="Expenses" stroke="hsl(var(--destructive))" strokeWidth={2} />
                <Line type="monotone" dataKey="Net" stroke="hsl(var(--muted-foreground))" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Monthly table */}
      <Card>
        <CardHeader><CardTitle>Month-by-month</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Refunds</TableHead>
                <TableHead className="text-right">Fees</TableHead>
                <TableHead className="text-right">Net rev.</TableHead>
                <TableHead className="text-right">Expenses</TableHead>
                <TableHead className="text-right">Op. income</TableHead>
                <TableHead className="text-right">Margin</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.months ?? []).slice().reverse().map((m) => {
                const buckets = feeBreakdownEntries(m.stripe_fees_breakdown);
                return (
                  <Fragment key={m.month}>
                    <TableRow>
                      <TableCell className="font-medium">{m.month}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(m.revenue_cents)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(m.refunds_cents)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(m.stripe_fees_cents)}{m.stripe_fees_source === "estimate" && <span className="text-xs text-muted-foreground"> est.</span>}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(m.net_revenue_cents)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(m.expenses_total_cents)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(m.operating_income_cents)}</TableCell>
                      <TableCell className="text-right">{m.margin_pct == null ? "—" : `${m.margin_pct.toFixed(1)}%`}</TableCell>
                    </TableRow>
                    {buckets.length > 0 && (
                      <TableRow key={`${m.month}-fees`} className="border-0 hover:bg-transparent">
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell colSpan={5} className="pt-0 pb-2">
                          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground justify-end">
                            <span className="opacity-70">Fee composition:</span>
                            {buckets.map(([label, cents]) => (
                              <span key={label} className="font-mono">
                                {label} <span className="text-foreground">{fmt(cents)}</span>
                              </span>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <SubscriberRosterCard environment={env} />

      <ExpensesPanel environment={env} onChanged={() => load(env)} />
    </div>
  );
};
